import { Banner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { WebsiteChatSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';
import {
  getMMdndEvents,
  MMdndContext,
} from '../../../../../common/helpers/MMdndContext';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { CustomerChatWidgetSettingsSection } from '../../../../features/configure/panels/sections/CustomerChatWidgetSettingsSection/CustomerChatWidgetSettingsSection';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { ChatWidgetPreviewChannelOption } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/ChatWidgetPreviewFrame/ChatWidgetPreviewFrame';
import { ChatWidgetTroubleshootingTool } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/ChatWidgetTroubleshootingTool/ChatWidgetTroubleshootingTool';
import { CustomerChatAdvancedSettings } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatAdvancedSettings/CustomerChatAdvancedSettings';
import { CustomerChatAppearanceEditor } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatAppearanceEditor/CustomerChatAppearanceEditor';
import { CustomerChatInstallInformation } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatInstallInformation/CustomerChatInstallInformation';
import { CustomerChatPreview } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatPreview/CustomerChatPreview';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './CustomerChatWidgetSlideoutEditor.module.css';

export type CustomerChatWidgetSlideoutEditorProps = {
  promoterId: number;
};

export const CustomerChatWidgetSlideoutEditor = ({
  promoterId,
}: CustomerChatWidgetSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const chatbot = useSelector(activeChatbotSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const promoter = useSelector(state => selectPromoterById(state, promoterId));
  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;
  const [promoters, setPromoters] = useState<PromoterModel[]>([]);
  const promoterCRUDActions = usePromoterCRUDActions();
  const [channel, setChannel] = useState<ChatWidgetPreviewChannelOption>(
    chatbot.chatWidgetChannel === 'facebook' ? 'facebook' : 'web_chat',
  );
  const openSections = {};
  const [isExpanded, toggleIsExpanded] = useState(openSections);
  const dndEvents = getMMdndEvents();
  const { setRef } = useSlideoutOnClose();

  const domainCRUDActions = useDomainCRUDActions();

  useEffect(() => {
    domainCRUDActions.retrieveAll(Number(facebookPageId));
    toggleIsExpanded({
      1: !openSections[1],
    });
  }, []);

  useEffect(() => {
    async function fetchItems() {
      const response = await promoterCRUDActions.retrieveAll(
        chatbotId,
        'customer_chat',
      );
      setPromoters(response);
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter || !page) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader promoter={promoter} title="Website Chat Widget" />
      <FreeTrialWarning />

      {!isMobile && (
        <Banner
          css={{
            container: styles.bannerContainer,
            header: styles.bannerHeader,
            collapsedHeader: styles.collapsedHeader,
            content: styles.bannerContent,
          }}
          withBannerHeader
          headerText="What is Website Chat Widget?"
          withLeftArea={false}
          showCloseIcon
          usedInSlideout
        >
          <WebsiteChatSlideoutBanner textColor="white" />
        </Banner>
      )}
      <SlideoutMenu.Content className={styles.content}>
        <MMdndContext.Provider value={dndEvents}>
          <div
            className={classnames(styles.configArea, {
              [styles.configAreaFullWidth]: isExpanded[2],
            })}
          >
            <CollapsibleContentContainerWithoutLocalState
              orderNumber="1"
              containerName="Install Website Chat"
              className={styles.collapsibleContainer}
              stepperStylingPlatform="webchat"
              useCircleNumberStyle
              isExpanded={isExpanded[1]}
              toggleIsExpanded={() =>
                toggleIsExpanded({
                  1: !openSections[1],
                })
              }
            >
              <div className={styles.blockWithPreview}>
                <CustomerChatWidgetSettingsSection
                  className={styles.settingsSection}
                  usedInSlideout
                />
                <CustomerChatInstallInformation
                  promoter={promoter}
                  usedInSlideout
                />
              </div>
            </CollapsibleContentContainerWithoutLocalState>

            <CollapsibleContentContainerWithoutLocalState
              orderNumber="2"
              containerName="Reply Instantly to users who send a message to your Chat Widget"
              expandedOnMount={false}
              className={styles.collapsibleContainer}
              stepperStylingPlatform="webchat"
              useCircleNumberStyle
              isExpanded={isExpanded[2]}
              toggleIsExpanded={() =>
                toggleIsExpanded({
                  2: !openSections[2],
                })
              }
            >
              <EmbeddableDialogueEditorWithPreview
                editPaneClassname={styles.dialogueEditPane}
                previewClassName={styles.dialogueEditPreviewPane}
                dialogueId={page.id}
                platforms={['web_chat']}
                usedInSlideout
                hideDialogueSelector
                withPreview
              />
            </CollapsibleContentContainerWithoutLocalState>
            <DragDropContext
              onDragStart={dndEvents.onDragStart}
              onDragUpdate={dndEvents.onDragUpdate}
              onDragEnd={dndEvents.onDragEnd}
            >
              <CollapsibleContentContainerWithoutLocalState
                orderNumber="3"
                containerName="Edit Chat Icon & Greeting"
                expandedOnMount={false}
                className={styles.collapsibleContainer}
                stepperStylingPlatform="webchat"
                useCircleNumberStyle
                isExpanded={isExpanded[3]}
                toggleIsExpanded={() =>
                  toggleIsExpanded({
                    3: !openSections[3],
                  })
                }
              >
                <div className={styles.blockWithPreview}>
                  <CustomerChatAppearanceEditor
                    promoter={promoter}
                    setPreviewChannel={setChannel}
                    onAlertTextClick={() =>
                      toggleIsExpanded({
                        1: !openSections[1],
                      })
                    }
                    usedInSlideout
                  />
                </div>
              </CollapsibleContentContainerWithoutLocalState>
            </DragDropContext>
            <CollapsibleContentContainerWithoutLocalState
              orderNumber="4"
              containerName="Edit Display Rules"
              expandedOnMount={false}
              className={styles.collapsibleContainer}
              stepperStylingPlatform="webchat"
              useCircleNumberStyle
              isExpanded={isExpanded[4]}
              toggleIsExpanded={() =>
                toggleIsExpanded({
                  4: !openSections[4],
                })
              }
            >
              <div className={styles.blockWithPreview}>
                <CustomerChatAdvancedSettings
                  promoter={promoter}
                  usedInSlideout
                />
              </div>
            </CollapsibleContentContainerWithoutLocalState>
            <CollapsibleContentContainerWithoutLocalState
              orderNumber="5"
              containerName="Troubleshooting"
              expandedOnMount={false}
              className={styles.collapsibleContainerLast}
              stepperStylingPlatform="webchat"
              useCircleNumberStyle
              isExpanded={isExpanded[5]}
              toggleIsExpanded={() =>
                toggleIsExpanded({
                  5: !openSections[5],
                })
              }
            >
              <ChatWidgetTroubleshootingTool
                promoter={promoter}
                usedInSlideout
              />
            </CollapsibleContentContainerWithoutLocalState>
          </div>
          {!isMobile && !isExpanded[2] && (
            <div className="customer-chat-editor__widget-preview_container">
              <CustomerChatPreview
                promoter={promoter}
                channel={channel}
                setChannel={setChannel}
              />
            </div>
          )}
        </MMdndContext.Provider>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
